import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { ClientValidationOperator } from "../../models/config/ClientValidationOperator";
import { ValidationRule } from "../../models/validation/ValidationRule";
import {
  TestedValidationResult,
  ValidationRuleTester,
} from "../../services/validation/ValidationRuleTester";
import "../../styles/validation/validationEditorList.css";
import { Button } from "../buttons/Button";
import { ErrorBoundary } from "../ErrorBoundary";
import { useAsync } from "../hooks/useAsync";
import { Icon } from "../Icon";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import { TTValidationAddRule } from "../tooltips/ConfigurationTooltips";
import { ExpandingTooltip } from "../tooltips/ExpandingTooltip";
import { ValidationEditorHeader } from "./ValidationEditorHeader";
import {
  ValidationRuleEditor,
  ValidationStatus,
  ValidationStatuses,
  ValidationStatusTT,
} from "./ValidationRuleEditor";

export { ValidationEditorList, Props, Title as ValidateTransformTitle };

/**
 * Throw an error if there are duplicate labels. Do not complain if there are many empty labels
 * @param rules
 * @returns
 */
function duplicateLabelError(rules: ValidationRule[]): void {
  const labels = rules.map((x) => x.label);
  if (labels.filter((x) => x.length === 0).length > 1) {
    // do not error when many blanks exist, let user set labels
    return;
  }
  const duplicateLabels = labels.filter(
    (label, i, arr) => arr.indexOf(label) !== i,
  );
  // complain about rule with duplicate names
  if (duplicateLabels.length > 0) {
    // get unique duplicates
    const uniqueDupes = duplicateLabels.filter(
      (label, i, arr) => arr.indexOf(label) === i,
    );
    const dialogMessage = `Validation rules already exists with the labels: '${uniqueDupes.join(", ")}'`;
    throw new Error(dialogMessage);
  }
}

function emptyRegexError(rules: ValidationRule[]): void {
  if (rules.find((x) => x.regex.trim() === "")) {
    const dialogMessage = `Please enter a regex for the Validation Rule`;
    throw new Error(dialogMessage);
  }
}

/**
 * Component too big !
 * TODO: offload rendering of list to calling component
 * TDID: remove props.header
 * TODO: fix rerendering issuees when ValidationRuleEditor component modified
 * TODO: move effective rules to be below template rules
 * TODO: hide Delete button
 */
type Props = {
  /** Title of main rules. */
  mainListTitle: string;
  fieldName: string;
  validationOperator: ClientValidationOperator;
  /**
   * If undefined, effective rules not shown
   */
  effectiveRulesOperator: ClientValidationOperator | undefined;
  effectiveRules: ValidationRule[];
  rules: ValidationRule[];
  /**
   * If undefined, should we hide the Test Value inputs ???
   */
  testValue?: string;
  update: (
    rules: ValidationRule[],
    validationOperator: ClientValidationOperator,
  ) => void;
  // trigger Openness.
  setOpen: (open: boolean) => void;
  /** If true, child components should be opened  */
  open: boolean;
  requireAllTooltip?: ExpandingTooltip;
};

// FIXME: add validation to these editors ( do not support empty label, description, or LEVEL )
const ValidationEditorList = (props: Props) => {
  const [showEffective, setShowEffective] = useState(
    props.effectiveRules.length !== 0,
  );
  const [showActual, setShowActual] = useState(true);
  const [validationOperator, setValidationOperator] = useState(
    props.validationOperator,
  );

  const [addNewRule, setAddNewRule] = useState<boolean>(false);

  const [testValue, setTestValue] = useState<string>(props.testValue ?? "");

  const [testedRules, setTestedRules] = useState<
    TestedValidationResult | undefined
  >(undefined);
  const [testedEffectiveRules, setTestedEffectiveRules] = useState<
    TestedValidationResult | undefined
  >(undefined);

  // TODO: result should be a class with method getRuleResult(ruleId: string) : Promise<String>;
  const [result, trigger, loading] = useAsync<
    undefined | TestedValidationResult
  >(
    // test ValidationRules effect on testValue
    async () =>
      new ValidationRuleTester(props.rules, validationOperator).validate(
        testValue,
      ),
    undefined,
  );

  // TODO: result should be a class with method getRuleResult(ruleId: string) : Promise<String>;
  const [resultEffective, triggerEffective, loadingEffective] = useAsync<
    undefined | TestedValidationResult
  >(
    // test ValidationRules effect on testValue
    async () => {
      if (props.effectiveRulesOperator) {
        return new ValidationRuleTester(
          props.effectiveRules,
          props.effectiveRulesOperator ?? "OR",
        ).validate(testValue);
      }
      return undefined;
    },
    undefined,
  );

  // set useAsync result in a stateobject we can undefine externally
  useEffect(() => setTestedRules(result), [result]);
  useEffect(() => setTestedEffectiveRules(resultEffective), [resultEffective]);
  // as rules are modified, clear results
  useEffect(() => {
    setTestedRules(undefined);
  }, [addNewRule]);
  // as text is modified,
  useEffect(() => {
    trigger();
    triggerEffective();
  }, [testValue, validationOperator]);

  /**
   * @param newRule If this is `null`, the rule will be deleted using the oldRuleId param
   */
  function mergeRules(
    rules: ValidationRule[],
    newRule: ValidationRule | null,
    oldRuleId: string,
  ): ValidationRule[] {
    if (newRule == null) {
      return rules.filter((x) => x.getRuleId() !== oldRuleId);
    }

    // try to find rule with oldRuleId
    const finalRules = [...rules];
    const ruleIndex = finalRules.findIndex((x) => x.getRuleId() === oldRuleId);
    if (ruleIndex == undefined) {
      console.warn("RuleIndex could not be found for modified ValidationRule");
      return rules;
    }
    finalRules.splice(ruleIndex, 1, newRule);

    return finalRules;
  }

  let globalErrorStatus: ValidationStatuses = "untested";
  if (props.rules.length > 0) {
    if (!loading && testedRules) {
      globalErrorStatus = testedRules.valid ? "valid" : "invalid";
    } else if (loading) {
      globalErrorStatus = "loading";
    }
  } else if (props.effectiveRules.length > 0) {
    if (!loadingEffective && testedEffectiveRules) {
      globalErrorStatus = testedEffectiveRules.valid ? "valid" : "invalid";
    } else if (loadingEffective) {
      globalErrorStatus = "loading";
    }
  }
  // function to update rules and check results
  function update(rules: ValidationRule[], operator: ClientValidationOperator) {
    trigger();
    triggerEffective();
    props.update(rules, operator);
  }

  return (
    <div className={"validationEditorList"}>
      {/* Show Test Rules button when all rules are confirmed */}
      <ValidationEditorHeader
        addBlankRule={() => {
          setAddNewRule(true);
          setTestedRules(undefined);
        }}
        disableAddRule={addNewRule}
        fieldName={props.fieldName}
        setValidationOperator={setValidationOperator}
        validationOperator={validationOperator}
        disabled={!props.open}
        requireAllTooltip={props.requireAllTooltip}
        status={
          globalErrorStatus != undefined ? (
            <ValidationStatusTT status={globalErrorStatus} />
          ) : undefined
        }
      >
        <div className={"test-validate"}>
          <input
            data-test-values
            type={"text"}
            value={testValue}
            onChange={(e) => {
              setTestedEffectiveRules(undefined);
              setTestedRules(undefined);
              setTestValue(e.target.value);
            }}
          />
        </div>
      </ValidationEditorHeader>

      <Title
        title={props.mainListTitle}
        count={props.rules.length}
        collapse={showActual}
        setCollapse={setShowActual}
      >
        {/* Add Rule Button */}
        <AdvancedTooltip overflow tooltipText={TTValidationAddRule}>
          {(tProps) => (
            <Button
              {...tProps}
              className={"addNewRule"}
              disabled={addNewRule}
              onClick={(e) => {
                setAddNewRule(true);
                setTestedRules(undefined);
                e.stopPropagation();
              }}
            >
              New Rule
            </Button>
          )}
        </AdvancedTooltip>
      </Title>

      <div className={`rules ${showActual ? "open" : "closed"}`}>
        {props.rules.map((rule, i) => {
          let testValidationStatus:
            | "loading"
            | "valid"
            | "invalid"
            | "untested" = "untested";
          if (loading) {
            testValidationStatus = "loading";
          } else if (testedRules) {
            const foundError = testedRules.errors[rule.getRuleId()];
            testValidationStatus = foundError ? "invalid" : "valid";
          }
          return (
            showActual && (
              <ValidationRuleEditor
                key={i}
                rule={rule}
                update={(newRule, oldRuleId) => {
                  const mergedRules = mergeRules(
                    props.rules,
                    newRule,
                    oldRuleId,
                  );
                  try {
                    duplicateLabelError(mergedRules);
                    emptyRegexError(mergedRules);
                  } catch (err) {
                    let message = "Unknown Error";
                    if (err instanceof Error) {
                      message = err.message;
                    }
                    // we'll proceed, but let's report it
                    return message;
                  }
                  setTestedRules(undefined);
                  update(mergedRules, validationOperator);
                  return;
                }}
                testValidationStatus={testValidationStatus}
                display={"edit"}
              />
            )
          );
        })}
        {addNewRule && (
          <ErrorBoundary>
            <ValidationRuleEditor
              display={"edit"}
              testValidationStatus={"untested"}
              rule={
                new ValidationRule({
                  uuid: undefined,
                  errorMessage: "",
                  label: "",
                  regex: "",
                })
              }
              update={(newRule, oldRuleId) => {
                if (newRule && !newRule.isConfigured()) {
                  return undefined;
                }
                let mergedRules;
                if (!newRule) {
                  mergedRules = props.rules;
                } else {
                  mergedRules = props.rules.concat([newRule]);
                }
                try {
                  duplicateLabelError(mergedRules);
                  emptyRegexError(mergedRules);
                } catch (err) {
                  let message = "Unknown Error";
                  if (err instanceof Error) {
                    message = err.message;
                  }
                  // we'll proceed, but let's report it
                  return message;
                }
                setTestedRules(undefined);
                update(mergedRules, validationOperator);
                setAddNewRule(false);
                props.setOpen(true);
                return undefined;
              }}
            />
          </ErrorBoundary>
        )}
      </div>
      <div
        className="effective"
        style={{
          display:
            props.effectiveRulesOperator == undefined ? "none" : "inherit",
        }}
      >
        {/* Hide collapse icon if no doc type rules exist */}
        <Title
          collapse={
            props.effectiveRules.length === 0 ? undefined : showEffective
          }
          count={props.effectiveRules.length}
          setCollapse={setShowEffective}
          title={"Document Type Validation Rules"}
        />
        <div className={"rules"}>
          {props.effectiveRules.map((rule, i) => {
            let testValidationStatus:
              | "loading"
              | "valid"
              | "invalid"
              | "untested" = "untested";
            if (loadingEffective) {
              testValidationStatus = "loading";
            } else if (testedEffectiveRules) {
              const foundError = testedEffectiveRules.errors[rule.getRuleId()];
              testValidationStatus = foundError ? "invalid" : "valid";
            }
            return (
              showEffective && (
                <ValidationRuleEditor
                  key={i + "" + showEffective}
                  disabled
                  display="edit"
                  rule={rule}
                  testValidationStatus={testValidationStatus}
                  update={() => ""}
                />
              )
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Title = (props: {
  /** Title for rules */
  title: string;
  /** Number of rules */
  count: number;
  /** If undefined, no collapse button shown. If true the chevron is in the closed position */
  collapse?: boolean;
  /** If undefined */
  setCollapse?: (collapse: boolean) => void;
  /** Shown after title */
  children?: ReactNode;
}) => {
  return (
    <span
      data-title
      onClick={() => props.setCollapse && props.setCollapse(!props.collapse)}
    >
      {props.collapse != undefined && (
        <Icon
          altText={"Open " + props.title}
          icon={props.collapse ? "chevron-up" : "chevron-down"}
        />
      )}
      {props.count == 0 && <h3>{`No ${props.title} Configured`}</h3>}
      {props.count > 0 && (
        <h3>{props.title + (props.count > 0 ? ` (x${props.count})` : "")}</h3>
      )}
      {props.children}
    </span>
  );
};

type RulesetValidProps = {
  valid: boolean | undefined;
};
/* Show */
const RulesetValidStatus = (props: RulesetValidProps) => {
  return (
    <span className={"status"}>
      {/* if rule is not configured, don't show anything */}
      {props.valid != undefined && !props.valid && (
        <Icon altText={`Ruleset failed`} color={"red"} icon={"times-circle"} />
      )}
      {props.valid != undefined && props.valid && (
        <Icon
          altText={`Ruleset passed`}
          color={"green"}
          icon={"check-circle"}
        />
      )}
    </span>
  );
};
